import { useState, useCallback } from 'react';
import { fetchApi } from '../api';
import kmsClientEncryption from './kmsClientEncryption';

export const useSignatureData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Save signature data (create/update)
  const saveSignature = useCallback(async (formData) => {
    setLoading(true);
    setError(null);
    
    try {
      const loggedInUser = sessionStorage.getItem("userEmail") || "";
      
      // Prepare payload with proper field mapping
      const payload = {
        salaryCode: formData.hrisSalaryCode || loggedInUser,
        company: formData.hrisCompanyCode || formData.csCompany || "GISPL",
        division: formData.hrisDivisionCode || formData.csDivision,
        name: formData.csName,
        address: formData.selectedAddressId,
        TelNo: formData.csTelNo,
        MobNo: formData.csMobNo,
        message: formData.csMessage,
        designation: formData.hrisDesignationCode || formData.csDesignation,
        empFunction: formData.hrisDepartmentCode || formData.csDivision,
        logCreatedBy: formData.csName,
        emailId: loggedInUser,
        band: formData.hrisBand || '',
      };

      console.log('[useSignatureData] Original payload:', payload);

      // Encrypt PII fields before sending
      const encryptedPayload = await kmsClientEncryption.encryptPIIFields(payload, ["TelNo", "MobNo"]);
      
      console.log('[useSignatureData] Encrypted payload:', {
        ...encryptedPayload,
        TelNo: encryptedPayload.TelNo ? '[ENCRYPTED]' : encryptedPayload.TelNo,
        MobNo: encryptedPayload.MobNo ? '[ENCRYPTED]' : encryptedPayload.MobNo
      });

      const response = await fetchApi("/api/signature/create", {
        method: "POST",
        body: JSON.stringify(encryptedPayload),
      });

      console.log('[useSignatureData] Save response:', response);
      return response;
    } catch (err) {
      console.error('[useSignatureData] Save error:', err);
      setError(err.message || 'Failed to save signature');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load signature data for display (plaintext)
  const loadSignatureForDisplay = useCallback(async (salaryCode, company) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('[useSignatureData] Loading signature for display:', { salaryCode, company });
      
      const response = await fetchApi(
        `/api/signature/signature-data?salaryCode=${salaryCode}&company=${company || ''}`
      );

      if (response.didError) {
        throw new Error(response.errorMessage || 'Failed to load signature data');
      }

      console.log('[useSignatureData] Display data loaded:', response.data);
      return response.data;
    } catch (err) {
      console.error('[useSignatureData] Load display error:', err);
      setError(err.message || 'Failed to load signature data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load signature data for editing (encrypted for client)
  const loadSignatureForEdit = useCallback(async (salaryCode, company) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('[useSignatureData] Loading signature for edit:', { salaryCode, company });
      
      // Get encrypted data from backend
      const response = await fetchApi(
        `/api/signature/signature-data?forClient=true&salaryCode=${salaryCode}&company=${company || ''}`
      );

      if (response.didError) {
        throw new Error(response.errorMessage || 'Failed to load signature data');
      }

      const encryptedData = response.data;
      console.log('[useSignatureData] Encrypted data received:', {
        ...encryptedData,
        cs_telephone_no: encryptedData?.cs_telephone_no ? '[ENCRYPTED]' : encryptedData?.cs_telephone_no,
        cs_mobile_no: encryptedData?.cs_mobile_no ? '[ENCRYPTED]' : encryptedData?.cs_mobile_no
      });

      // If we have encrypted data, set up decryption key and decrypt
      if (encryptedData?.encryptedDataKey) {
        // Set the data key for decryption
        kmsClientEncryption.encryptedDataKey = encryptedData.encryptedDataKey;
        
        // For editing, we'll actually return plaintext data
        // The encryption will happen again when saving
        return await loadSignatureForDisplay(salaryCode, company);
      }

      return encryptedData;
    } catch (err) {
      console.error('[useSignatureData] Load edit error:', err);
      setError(err.message || 'Failed to load signature data for editing');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadSignatureForDisplay]);

  return {
    loading,
    error,
    saveSignature,
    loadSignatureForDisplay,
    loadSignatureForEdit,
    clearError: () => setError(null)
  };
};

export default useSignatureData;